import { getState } from '~src/state';

/**
 * Configuration for spell check control sizes
 */
export const controlConfig = {
  baseSize: {
    x: 160,
    y: 56,
  },
  // Target visual size in pixels (how big it should appear on screen)
  targetVisualSize: {
    x: 120,
    y: 42,
  },
  scaling: {
    small: {
      threshold: 800,
      factor: 1.3,
    },
    medium: {
      threshold: 1200,
      factor: 1.1,
    },
  },
  position: {
    x: 0,
    y: -0.5,
    baseOffsetY: -50,
    baseOffsetX: 120,
  },
  padding: 10,
};

/**
 * Get control sizes based on canvas zoom and dimensions
 * This ensures the button appears consistent regardless of canvas size/zoom
 * @returns {object} Control sizes
 */
export function getControlSizes() {
  // Get the current canvas zoom from state
  const currentZoom = getState('currentCanvasZoom', 1);

  // Start with target visual size (how big we want it to appear)
  let visualSizeX = controlConfig.targetVisualSize.x;
  let visualSizeY = controlConfig.targetVisualSize.y;

  // Get canvas dimensions
  const canvasWidth = window.canvas?.getWidth ? window.canvas.getWidth() : 1000;
  const canvasHeight = window.canvas?.getHeight
    ? window.canvas.getHeight()
    : 1000;

  // Calculate a reference dimension (use the smaller dimension for consistency)
  const referenceDimension = Math.min(canvasWidth, canvasHeight);

  // Calculate a scaling factor based on canvas size
  // For a 1000px canvas, this would be 1.0
  // For a 2000px canvas, this would be 2.0
  const canvasScale = referenceDimension / 1000;

  // Apply inverse zoom scaling
  // If zoom is 0.5 (canvas appears smaller), we need to make the button 2x bigger
  // If zoom is 2.0 (canvas appears larger), we need to make the button 0.5x smaller
  const zoomCompensation = 1 / currentZoom;

  // Calculate final size
  // The button needs to be larger on larger canvases to maintain visual consistency
  let sizeX = visualSizeX * canvasScale * zoomCompensation;
  let sizeY = visualSizeY * canvasScale * zoomCompensation;

  // Apply additional scaling for very small or very large canvases
  if (canvasWidth < controlConfig.scaling.small.threshold) {
    // For small canvases, make the button slightly smaller to not overwhelm
    sizeX = sizeX * 0.8;
    sizeY = sizeY * 0.8;
  } else if (canvasWidth > 3000) {
    // For very large canvases, cap the maximum size
    const maxScale = 3000 / canvasWidth;
    sizeX = sizeX * Math.max(maxScale, 0.7);
    sizeY = sizeY * Math.max(maxScale, 0.7);
  }

  // Ensure minimum size for usability
  sizeX = Math.max(sizeX, 80);
  sizeY = Math.max(sizeY, 28);

  return { sizeX: Math.round(sizeX), sizeY: Math.round(sizeY) };
}

/**
 * Get scaled position offsets based on current control size
 * @returns {object} Position offsets
 */
export function getControlPositionOffsets() {
  const { sizeX, sizeY } = getControlSizes();

  // Scale offsets proportionally to control size
  // Base offsets are for 160x56 button
  const offsetX =
    (controlConfig.position.baseOffsetX * sizeX) / controlConfig.baseSize.x;
  const offsetY =
    (controlConfig.position.baseOffsetY * sizeY) / controlConfig.baseSize.y;

  return {
    offsetX: Math.round(offsetX),
    offsetY: Math.round(offsetY),
  };
}
